import PropTypes from 'prop-types';
import * as React from 'react';
import cx from 'classnames';
import styles from './InlineOfferMessage.css';
import productOfferImage from 'images/product-offer.svg';
import tickGreenImage from 'images/tick-green.svg';
import StringWidget, { stringTemplate } from 'components/StringWidget/StringWidget';
import genUniqueId from 'utils/uniqueId';
import { ADJUSTMENT_TYPE, ADJUSTMENT_SUB_TYPE, ADJUSTMENT_STATUS, PROVIDER } from 'pages/PaymentsPage/PaymentOptions/constants';
import Row from 'pages/PaymentsPage/PaymentOptions/EMI/ItemLevelBreakup/Row';
import { Label, Value } from 'pages/PaymentsPage/PaymentOptions/EMI/ItemLevelBreakup/Columns';
import Help from 'components/Help/Help';
import ContentGenerator from 'components/ContentGenerator/ContentGenerator';
import { get } from 'lodash';
// TODO: Migrate paymenthelper to tsx and move this function there, and merge the ones in Tenure.tsx
export function formatAmount(rawValue) {
    return Number(rawValue || 0) / 100.0;
}
// TODO: Migrate paymenthelper to tsx and move this function there, and merge the ones in Tenure.tsx
function toString(rawValue) {
    if (!rawValue || typeof rawValue.toLocaleString !== 'function') {
        return '';
    }
    return rawValue.toLocaleString('en-IN');
}
// The offers that get included in the modal window when the offer message is clicked are:
//
// 1. Offers that are applicable
// 2. Offers that are partially applicable, failed or exhausted and their
//    display_failure_description is NOT set to false
export const filterOffersForModalGeneration = (adjustmentList) => {
    if (!adjustmentList || !adjustmentList.length)
        return [];
    const filteredOffers = adjustmentList.filter(adjustment => {
        if (adjustment.adjustment_status === ADJUSTMENT_STATUS.APPLICABLE) {
            return true;
        }
        else {
            return adjustment.display_failure_description === true;
        }
    });
    return filteredOffers;
};
export const filterNonNCEOffers = (adjustmentList) => {
    if (!adjustmentList || !adjustmentList.length)
        return [];
    const filteredOffers = adjustmentList.filter(adjustment => {
        const adjustmentSubType = get(adjustment, 'offer_details.adjustment_sub_type');
        return (adjustmentSubType !== ADJUSTMENT_SUB_TYPE.EMI_FULL_INTEREST_WAIVER &&
            adjustmentSubType !== ADJUSTMENT_SUB_TYPE.NBFC_ZERO_INTEREST);
    });
    return filteredOffers;
};
// If all offers are one of fully, failed and display flag is set to
// false, we need to skip generating the offer message.
//
// This function returns filtered offers that need to be checked againts the
// entire set of adjustmentList. If both are the same, then we need to skip
// generating the message altogether.
export const filterOffersForMessageGeneration = (adjustmentList) => {
    if (!adjustmentList || !adjustmentList.length)
        return [];
    const filteredOffers = adjustmentList.filter(adjustment => !(adjustment.adjustment_status === ADJUSTMENT_STATUS.APPLICABLE ||
        adjustment.adjustment_status === ADJUSTMENT_STATUS.PARTLY_EXHAUSTED) && adjustment.display_failure_description === false);
    return filteredOffers;
};
export const filterApplicableOffers = (adjustmentList) => {
    if (!adjustmentList || !adjustmentList.length)
        return [];
    return adjustmentList.filter(offer => offer.adjustment_status !== ADJUSTMENT_STATUS.FAILED && offer.adjustment_status !== ADJUSTMENT_STATUS.EXHAUSTED);
};
const OfferFailureDescription = (props) => {
    const { adjustment, provider, tncLinkUrl } = props;
    const adjustmentId = get(adjustment, 'offer_details.adjustment_id');
    const failureDescription = get(adjustment, 'failure_description', '') || '';
    // We're trying to add styling based on whether the failure description is
    // shown or not. If it's shown, the TnC link shouldn't have any margin. If
    // not, it should have a margin equal to that of the offer description
    // message. In both cases though, the text style should look same as that
    // of the offer description message
    const helpClassName = cx(styles['tnc-link'], {
        [cx(styles['margin-left'], styles['offer-failure-description'], styles['bolder'])]: !failureDescription && !!adjustmentId
    });
    const helpWithFailureDescriptionClassName = cx(styles['offer-failure-description'], styles['bolder']);
    // For PhonePe powered offer terms and conditions we load an iframe, with the given url;
    const contentGeneratorType = provider === PROVIDER.PHONEPE && tncLinkUrl ? 'iframe' : 'deferred';
    // For Flipkart powered offers, we hit a payzippy's relative url to load the terms and conditions
    const contentGeneratorData = provider === PROVIDER.FLIPKART ? `/fkpay/api/v1/offers/${adjustmentId}/terms` : tncLinkUrl;
    const requestToPayzippy = tncLinkUrl === '' && provider === PROVIDER.FLIPKART;
    // Add the left margin styling only if the help text is shown as a
    // standalone link, without failure description
    const help = provider === PROVIDER.PHONEPE && tncLinkUrl === '' ? null : (<Help text={stringTemplate('giftcardTnC')} position="top" className={helpClassName}>
				<ContentGenerator config={[
        {
            type: contentGeneratorType,
            data: contentGeneratorData,
            requestToPayzippy,
            responseKey: 'terms'
        }
    ]}/>
			</Help>);
    if (failureDescription) {
        const descriptionProps = { failureDescription };
        if (adjustmentId) {
            Object.assign(descriptionProps, { help });
        }
        return (<StringWidget strKey="offerFailureDescription" className={helpWithFailureDescriptionClassName} attributes={descriptionProps}/>);
    }
    if (adjustmentId) {
        return help;
    }
    return null;
};
const OfferDetailBreakup = props => {
    const { adjustmentList } = props;
    // The offers we need to show in the modal are the same things we consider
    // for generating the inline offer message.
    const filteredOffers = filterOffersForModalGeneration(adjustmentList);
    const messages = filteredOffers.map((adjustment, index) => {
        const title = get(adjustment, 'offer_details.display_text') || '';
        const offerAmount = formatAmount(adjustment.amount_delta);
        const provider = get(adjustment, 'offer_details.provider', PROVIDER.FLIPKART);
        const tncLinkUrl = get(adjustment, 'offer_details.tnc_link_url', '');
        const breakupObj = [
            { minOrderValue: get(adjustment, 'offer_details.offer_txn_limits.min_txn_value') },
            { maxOrderValue: get(adjustment, 'offer_details.offer_txn_limits.max_txn_value') },
            { maxDiscountPerTxn: get(adjustment, 'offer_details.offer_txn_limits.max_discount_per_txn') },
            { maxDiscountPerCard: get(adjustment, 'offer_details.offer_aggregation_limits.max_discount_per_card') },
            { maxDiscountPerAccount: get(adjustment, 'offer_details.offer_aggregation_limits.max_discount_per_account') }
        ];
        const maxTxnsForOffer = toString(get(adjustment, 'offer_details.offer_aggregation_limits.max_txns_for_offer'));
        let key = get(adjustment, 'offer_details.adjustment_id', genUniqueId());
        key = `${key}-${index}`;
        // TODO:
        // 1. Figure out if we have to show a trailing dot for title
        // 2. The Value component's amountStr prop is a string, but we are using a
        //    number here to ensure support for avoiding render if the value is
        //    0. Doing a conversion to string returns '0' which will endup getting
        //    shown.
        return (<div key={key} className={styles['offer-description-wrapper']}>
				<img src={productOfferImage} className={styles['offer-tag-icon']}/>
				{title ? <span className={styles['offer-description']}>{title}</span> : null}
				<table className={cx(styles['table'], styles['left-padding'])}>
					<colgroup>
						<col span={1} className={styles['column-label']}/>
						<col span={1} className={styles['column-amount']}/>
					</colgroup>
					<tbody>
						{breakupObj.map(obj => {
            const strKey = Object.keys(obj)[0];
            const rawAmount = obj[strKey];
            return (<Row key={strKey} className={styles['row']}>
									<Label strKey={strKey} className={styles['column-label']}/>
									<Value rawAmount={rawAmount} className={styles['column-amount']}/>
								</Row>);
        })}
						<Row className={styles['row']}>
							<Label strKey="maxTxnsForOffer" className={styles['column-label']}/>
							<Value amountStr={maxTxnsForOffer} className={cx(styles['txns-for-offer'], styles['column-amount'])} addRupee={false}/>
						</Row>
						<Row className={cx(styles['row'], styles['bolder'], {
            [styles['green']]: offerAmount > 0,
            [styles['yellow']]: offerAmount === 0
        })}>
							<Label strKey="youGetOffer" className={styles['column-label']}/>
							<Value amountStr={offerAmount.toString()} className={cx(styles['txns-for-offer'], styles['column-amount'])}/>
						</Row>
					</tbody>
				</table>
				<OfferFailureDescription adjustment={adjustment} tncLinkUrl={tncLinkUrl} provider={provider}/>
			</div>);
    });
    return (<div className={styles['breakup-wrapper']}>
			<h3 className={styles['breakup-heading']}>
				<StringWidget strKey="offerDetails"/>
			</h3>
			{messages}
		</div>);
};
class OfferDetailLink extends React.Component {
    constructor() {
        super(...arguments);
        this.handleClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Not sure if the get access is necessary. But there is no guarantee that
            // the tooltip will be present by the time this code path gets called. the
            // hoverOutTimeout of Tooltip component should help, but not entirely sure.
            const showModal = get(this, 'context.modal.show');
            const adjustmentList = get(this, 'props.adjustmentList', []);
            if (typeof showModal === 'function' && adjustmentList.length) {
                const modalOptions = {
                    closeOnEscape: true,
                    closeOnOverlayClick: true,
                    closeButton: true,
                    scrollBarOnBody: true,
                    children: <OfferDetailBreakup adjustmentList={adjustmentList}/>
                };
                this.context.modal.show(modalOptions);
            }
            const { onClick } = this.props;
            if (onClick) {
                onClick();
            }
        };
    }
    render() {
        // This makes the component reusable for both the details link in the
        // popup, and the main offer message display. In both cases, clicking on
        // the items will open up the same modal window.
        // TODO: Decide if "details" is what we want to show. Something like
        // "breakup" makes more sense, may be.
        const label = this.props.children ? this.props.children : 'Details';
        const finalProps = {
            className: styles['breakup-link'],
            onClick: this.handleClick
        };
        if (this.props.id) {
            Object.assign(finalProps, { id: this.props.id });
        }
        return <a {...finalProps}>{label}</a>;
    }
}
OfferDetailLink.contextTypes = {
    modal: PropTypes.object
};
// Though this function is exported, do not use this without proper error
// checks. These are not added deliberately to keep the code simple. The user
// function in this file already maintains the proper inteface required for
// this function to work.
export function PartiallyOrExhaustedOfferTitle(props) {
    const instantOffers = props.aggregatedOffers.instantOffers;
    const cashBacks = props.aggregatedOffers.cashBacks;
    let title = stringTemplate('offerNotAppliedTitle');
    if (instantOffers.available.count === 0) {
        // we have no instant offers, may be only cashbacks. If both are not
        // available, then use the default message.
        //
        // In two cases we show a contextual cashback message that includes the
        // amount:
        //
        // 1. If there is a single cashback, and is a partially exhausted cashback
        // 2. If there are multiple cashbacks, and ALL of the cashbacks are NOT
        //    completely exhausted
        //
        // In the aggregated object, the total offer count will be equal to fully
        // exhausted offer count if all of the offers are exhausted. So we can rely
        // only on that check. And total "exhausted" offers are ones that are fully
        // exhausted and failed.
        //
        // TODO: This logic is basically same as the one that's shown as the offer
        // text. May be combine both.
        const { wallet, bank } = cashBacks.available;
        if (wallet.count === 0 && bank.count > 0) {
            title = stringTemplate('bankCashBackAppliedTitle', { amount: toString(cashBacks.available.amount) });
        }
        if (wallet.count > 0 && bank.count === 0) {
            title = stringTemplate('walletCashBackAppliedTitle', { amount: toString(cashBacks.available.amount) });
        }
    }
    else if (cashBacks.available.count === 0) {
        // we have one or more instant offers, may be zero cashbacks. Logic is same
        // as in the case of cashbacks
        if (instantOffers.count !== instantOffers.exhausted.count) {
            title = stringTemplate('instantOfferAppliedTitle', { amount: toString(instantOffers.available.amount) });
        }
    }
    return <p className={styles['not-applicable-offer-title']}>{title}</p>;
}
const ineligibleAdjustmentTypes = [
    ADJUSTMENT_TYPE.CASHBACK_ON_CARD,
    ADJUSTMENT_TYPE.CASHBACK_IN_WALLET,
    ADJUSTMENT_TYPE.CASHBACK_IN_BANK
];
const ineligibleAdjustmentSubTypes = [
    ADJUSTMENT_SUB_TYPE.EMI_FULL_INTEREST_WAIVER,
    ADJUSTMENT_SUB_TYPE.NBFC_ZERO_INTEREST
];
export const aggregateOffers = (adjustmentList) => {
    const INITIAL_STATE = {
        instantOffers: {
            amount: 0,
            count: 0,
            available: { amount: 0, count: 0 },
            exhausted: { amount: 0, count: 0 }
        },
        cashBacks: {
            amount: 0,
            count: 0,
            available: {
                amount: 0,
                count: 0,
                wallet: { amount: 0, count: 0 },
                bank: { amount: 0, count: 0 }
            },
            exhausted: {
                amount: 0,
                count: 0,
                wallet: { amount: 0, count: 0 },
                bank: { amount: 0, count: 0 }
            }
        }
    };
    if (!adjustmentList || !adjustmentList.length)
        return INITIAL_STATE;
    // This keymap contains which key of the "aggregate" object gets operated on
    // for the corresponding adjustment_status. For instance, we can aggregate
    // both PARTLY_EXHAUSTED and APPLICABLE offers as "available" and use that
    // key when showing the corresponding amounts.
    const keyMap = {
        [ADJUSTMENT_STATUS.APPLICABLE]: 'available',
        [ADJUSTMENT_STATUS.PARTLY_EXHAUSTED]: 'available',
        [ADJUSTMENT_STATUS.EXHAUSTED]: 'exhausted',
        [ADJUSTMENT_STATUS.FAILED]: 'exhausted'
    };
    return adjustmentList.reduce((aggregate, offer) => {
        const key = keyMap[offer.adjustment_status];
        if (!key)
            return aggregate;
        const adjustmentType = get(offer, 'offer_details.adjustment_type');
        const adjustmentSubType = get(offer, 'offer_details.adjustment_sub_type');
        const amount = formatAmount(offer.amount_delta);
        if (adjustmentType === ADJUSTMENT_TYPE.CASHBACK_IN_WALLET) {
            aggregate.cashBacks[key].wallet.amount += amount;
            aggregate.cashBacks[key].amount += amount;
            aggregate.cashBacks.amount += amount;
            aggregate.cashBacks[key].wallet.count++;
            aggregate.cashBacks[key].count++;
            aggregate.cashBacks.count++;
        }
        if (adjustmentType === ADJUSTMENT_TYPE.CASHBACK_ON_CARD || adjustmentType === ADJUSTMENT_TYPE.CASHBACK_IN_BANK) {
            aggregate.cashBacks[key].bank.amount += amount;
            aggregate.cashBacks[key].amount += amount;
            aggregate.cashBacks.amount += amount;
            aggregate.cashBacks[key].bank.count++;
            aggregate.cashBacks[key].count++;
            aggregate.cashBacks.count++;
        }
        const isInstantOffer = ineligibleAdjustmentTypes.indexOf(adjustmentType) < 0 &&
            ineligibleAdjustmentSubTypes.indexOf(adjustmentSubType) < 0;
        if (isInstantOffer) {
            aggregate.instantOffers[key].amount += amount;
            aggregate.instantOffers.amount += amount;
            aggregate.instantOffers[key].count++;
            aggregate.instantOffers.count++;
        }
        return aggregate;
    }, INITIAL_STATE);
};
export const calculateTotalCashBacks = (adjustmentList = []) => {
    // TODO: Move the aggregation step to the caller function, at which point
    // this entire function can be removed.
    const aggregatedOffers = aggregateOffers(adjustmentList);
    return aggregatedOffers.cashBacks.available;
};
export const calculateTotalInstantOffers = (adjustmentList = []) => {
    // TODO: Move the aggregation step to the caller function, at which point
    // this entire function can be removed.
    const aggregatedOffers = aggregateOffers(adjustmentList);
    return aggregatedOffers.instantOffers.available;
};
export const calculateEmiDiscount = (adjustmentList) => {
    if (!adjustmentList || !adjustmentList.length)
        return 0;
    const filteredOffers = filterApplicableOffers(adjustmentList).filter(offer => offer.offer_details.adjustment_type === ADJUSTMENT_TYPE.INSTANT_DISCOUNT &&
        offer.offer_details.adjustment_sub_type === ADJUSTMENT_SUB_TYPE.EMI_FULL_INTEREST_WAIVER);
    // TODO: Merge this with the previous filter loop
    const discount = filteredOffers.reduce((totalDiscount, offer) => {
        return totalDiscount + formatAmount(offer.amount_delta);
    }, 0);
    return discount;
};
// The offer message is generated based on the type of offer, and the number of
// applicable offers. In the default case, the message is:
//
// <num of offers> offers applicable
//
// For any other case, depending on the combination of offer types, the
// following matrix is followed:
// ┌────────────────────────────┬────────────────┬────────────────────────────────────────────┐
// │                 Offer Types│  Offer Count   │                                     Message│
// ├────────────────────────────┼────────────────┼────────────────────────────────────────────┤
// │       wallet cash back only│ single/multiple│      Rs.<amount> Wallet Cashback applicable│
// │         bank cash back only│ single/multiple│  Rs.<amount> Bank Cashback offer applicable│
// │          instant offer only│ single/multiple│     Rs.<amount> instant discount applicable│
// └────────────────────────────┴────────────────┴────────────────────────────────────────────┘
// Note that the part with "applied/applicable" depends on the payment type.
// Except for new cards, this is almost always applied if the payment
// instrument is selected.
export const generateInlineOfferMessage = (adjustmentList, applied = false) => {
    // TODO: This is being computed already in the main component. Figure out a way to avoid this.
    const aggregatedOffers = aggregateOffers(adjustmentList);
    const { instantOffers, cashBacks } = aggregatedOffers;
    // The "count" of offers that are applicable are the ones with
    // adjustment_status of PARTLY_EXHAUSTED and APPLICABLE
    const totalAvailableOfferCount = instantOffers.available.count + cashBacks.available.count;
    const suffix = applied ? 'applied.' : 'applicable.';
    const offer = totalAvailableOfferCount > 1 ? 'offers' : 'offer';
    // When there are mixed offers, the default case is to show the count of
    // offers.
    let message = stringTemplate('offerApplicable', { suffix, offer, count: totalAvailableOfferCount });
    if (instantOffers.available.count === 0) {
        // There are no instant offers. May be only cash backs. We also need to
        // check if all the offers are of the same kind. If they are, we should
        // show the total amount, and if not, the count of offers.
        const { wallet, bank } = cashBacks.available;
        if (wallet.count === 0 && bank.count > 0) {
            return stringTemplate('bankCashBack', { amount: bank.amount.toLocaleString('en-IN'), suffix });
        }
        if (wallet.count > 0 && bank.count === 0) {
            return stringTemplate('walletCashBack', { amount: wallet.amount.toLocaleString('en-IN'), suffix });
        }
    }
    else if (cashBacks.available.count === 0) {
        // There is only instant offer at this point. Multiple or Single.
        return stringTemplate('instantOffer', { amount: instantOffers.amount.toLocaleString('en-IN'), suffix });
    }
    return message;
};
export default function InlineOfferMessage(props) {
    const { eligibleList: adjustmentList, className, messageClassName, applied = false } = props;
    if (!adjustmentList || !adjustmentList.length)
        return null;
    const aggregatedOffers = aggregateOffers(adjustmentList);
    const { instantOffers, cashBacks } = aggregatedOffers;
    // TODO: Decide on whether "applicable" denotes both applicable and partially
    // exhausted (which it does here), or pick some other variable name
    // This variable has both PBOs or cashbacks
    const applicableOffersCount = instantOffers.available.count + cashBacks.available.count;
    // The "count" of offers that are applicable are the ones with
    // adjustment_status of PARTLY_EXHAUSTED and APPLICABLE
    const applicableOffersAmount = instantOffers.available.amount + cashBacks.available.amount;
    // All offers are exhausted for a non-empty adjustmentList if there's NO
    // available offer
    const areAllOffersExhausted = applicableOffersCount === 0;
    const handleClick = e => {
        e.stopPropagation();
        e.preventDefault();
    };
    // Don't render anything when:
    // 1. if all offers are one of partially, fully, failed and display flag is
    //    set to false
    // 2. there are no offers (same as adjustment list being empty, which is
    //    covered above)
    // 3. if total offer amount is zero. This will be the case when Bajaj NCE
    //    is present. There will be offers in the adjustment list, but the
    //    aggregate amount_delta will be 0. But check this only when there's
    //    atleast one applicable, or partially exhausted offer
    const skipDisplayFlagsCount = filterOffersForMessageGeneration(adjustmentList).length;
    if (skipDisplayFlagsCount === adjustmentList.length) {
        return null;
    }
    if (applicableOffersCount) {
        if (applicableOffersAmount === 0) {
            return null;
        }
    }
    else {
        const exhaustedOffersCount = instantOffers.exhausted.count + cashBacks.exhausted.count;
        // this block also contains case where there might be NCE offers
        // because we don't include them in the aggregation.
        // FIXME: This depends on an assumption that any NCE offer comes with
        // applicable type, and so the skip display flag count will be counted
        // for exhausted or failed offers.
        if (exhaustedOffersCount === skipDisplayFlagsCount)
            return null;
    }
    {
        // If all Non-NCE offers are exhausted, and all of those have the skip
        // display flag set then don't display anything.
        const nonNCEOffers = filterNonNCEOffers(adjustmentList);
        const skipDisplayFlagsCount = filterOffersForMessageGeneration(nonNCEOffers).length;
        if (skipDisplayFlagsCount === nonNCEOffers.length) {
            return null;
        }
    }
    const knowmore = (<StringWidget strKey="knowmoreTitlelized" className={cx(styles['offer-description'], styles['blue'])}/>);
    const details = <StringWidget strKey="details" className={cx(styles['offer-description'], styles['blue'])}/>;
    const currentOfferLabelImage = applied ? tickGreenImage : productOfferImage;
    // TODO: If for some reason the data is in the wrong format, we end up
    // showing a inline offer message with "0 offer applicable. Details" message,
    // which should not be the case.
    return (<div className={cx(styles['offer-message'], className)} onClick={handleClick}>
			{areAllOffersExhausted ? null : <img src={currentOfferLabelImage}/>}
			{areAllOffersExhausted ? (<OfferDetailLink adjustmentList={adjustmentList} onClick={props.onClick}>
					<StringWidget strKey="offerNotApplicable" className={cx(messageClassName, styles['yellow'], styles['offer-description'])} attributes={{ why: knowmore }}/>
				</OfferDetailLink>) : (<OfferDetailLink adjustmentList={adjustmentList} onClick={props.onClick}>
					<span className={cx(messageClassName, styles['offer-description'], styles['green'])}>
						{generateInlineOfferMessage(adjustmentList, applied)}
						{details}
					</span>
				</OfferDetailLink>)}
		</div>);
}
